import React from 'react';
import SlideLayout, { SlideTitle, SlideContent } from '../components/SlideLayout';
import { GitBranch, Vote, BarChart3 } from 'lucide-react';

const SelfConsistencySlide = () => {
  return (
    <SlideLayout maxWidth="6xl">
      <SlideTitle 
        subtitle="Generate multiple reasoning paths and vote on the answer"
      >
        Self-Consistency
      </SlideTitle>
      
      <SlideContent>
        <div className="grid lg:grid-cols-2 gap-8">
          {/* Technique explanation */}
          <div>
            <h3 className="font-heading text-lg font-semibold text-foreground mb-4">Technique</h3>
            
            <div className="slide-card mb-4">
              <div className="flex items-start gap-3">
                <GitBranch className="w-5 h-5 text-primary flex-shrink-0 mt-1" />
                <div>
                  <p className="font-medium text-foreground">Multiple Reasoning Paths</p>
                  <p className="text-sm text-muted-foreground mt-1">
                    Generate 5+ different reasoning chains with temperature sampling
                  </p>
                </div>
              </div>
            </div>
            
            <div className="slide-card mb-4">
              <div className="flex items-start gap-3">
                <Vote className="w-5 h-5 text-secondary flex-shrink-0 mt-1" />
                <div>
                  <p className="font-medium text-foreground">Vote on Answer</p>
                  <p className="text-sm text-muted-foreground mt-1">
                    Select the most consistent answer across paths
                  </p>
                </div>
              </div>
            </div>
            
            <div className="slide-card">
              <div className="flex items-start gap-3">
                <BarChart3 className="w-5 h-5 text-accent flex-shrink-0 mt-1" />
                <div>
                  <p className="font-medium text-foreground">Confidence Score</p>
                  <p className="text-sm text-muted-foreground mt-1">
                    Agreement ratio indicates reliability
                  </p>
                </div>
              </div>
            </div>
            
            <div className="mt-6 p-4 rounded-xl bg-chart-4/10 border border-chart-4/20">
              <p className="text-sm text-muted-foreground">
                <strong className="text-chart-4">Trade-off:</strong> Higher accuracy vs. 5x compute cost
              </p>
            </div>
          </div>
          
          {/* Example visualization */}
          <div className="slide-card">
            <h4 className="font-heading text-lg font-semibold text-foreground mb-4">NOC Incident Analysis</h4>
            
            <div className="space-y-3">
              {[
                { path: 1, answer: 'Network interface errors', matches: true },
                { path: 2, answer: 'Network connectivity issue', matches: false },
                { path: 3, answer: 'Network interface errors', matches: true },
                { path: 4, answer: 'High error rate on interface', matches: true },
                { path: 5, answer: 'Network interface errors', matches: true },
              ].map((item) => (
                <div 
                  key={item.path}
                  className={`flex items-center justify-between p-3 rounded-lg border ${
                    item.matches 
                      ? 'bg-accent/10 border-accent/30' 
                      : 'bg-muted/30 border-muted/50'
                  }`}
                >
                  <span className="text-sm">
                    <span className="text-muted-foreground">Path {item.path}:</span>{' '}
                    <span className={item.matches ? 'text-foreground' : 'text-muted-foreground'}>
                      {item.answer}
                    </span>
                  </span>
                  {item.matches && <span className="text-accent text-sm">✓</span>}
                </div>
              ))}
            </div>
            
            <div className="mt-6 p-4 rounded-lg bg-accent/20 border border-accent/30">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Root Cause</p>
                  <p className="font-heading font-semibold text-foreground">Network interface errors</p>
                </div>
                <div className="text-right">
                  <p className="text-sm text-muted-foreground">Confidence</p>
                  <p className="font-heading font-semibold text-accent text-2xl">80%</p>
                </div>
              </div>
              <p className="text-xs text-muted-foreground mt-2">4/5 reasoning paths agreed</p>
            </div>
          </div>
        </div>
      </SlideContent>
    </SlideLayout>
  );
};

export default SelfConsistencySlide;
